import { GoogleMap, Circle } from '@react-google-maps/api'
import { useTheme, Box, Typography, Avatar, Divider, IconButton } from '@mui/material'
import MapMarker from 'src/assets/files/map-marker.svg'
import MapMarkerGreen from 'src/assets/files/map-marker-green.svg'
import { useTranslation } from 'react-i18next'
import { useStyles } from './styles'
import { CustomMarker } from './custom-marker.component'
import { MutableRefObject, useState } from 'react'
import { ICoords, IStepLocation } from 'src/types'
import { AppEvents, handleMapTypeIdChange } from 'admin-ui/src/constants'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

interface IProps {
  center: ICoords
  radius: number
  scannerLocation: ICoords
  scannerName?: string
  stepName?: string
  stepLocations: IStepLocation[]
  mapRef: MutableRefObject<google.maps.Map | null>
}

export const ScanMap = ({
  center,
  scannerLocation,
  scannerName,
  stepLocations,
  mapRef,
}: IProps) => {
  const theme = useTheme()
  const classes = useStyles()
  const [t] = useTranslation()

  const [mapBoundsUpdated, setMapBoundsUpdated] = useState(false)

  const handleLoad = (map: google.maps.Map) => {
    mapRef.current = map

    handleMapTypeIdChange(map)

    if (!mapBoundsUpdated) {
      const bounds = new window.google.maps.LatLngBounds()
      ;[scannerLocation, ...stepLocations].forEach((marker) => {
        bounds.extend({ lat: marker.lat, lng: marker.lng })
      })

      map.fitBounds(bounds)
      setMapBoundsUpdated(true) // Set to true after initial map load to avoid further updates
    }
  }

  return (
    <GoogleMap
      onMapTypeIdChanged={() => {
        handleMapTypeIdChange(mapRef?.current)
      }}
      mapContainerClassName={classes.map}
      center={center}
      options={{
        clickableIcons: false,
        streetViewControl: false,
      }}
      onLoad={handleLoad}
    >
      {(scannerLocation.lat !== 0 || scannerLocation.lng !== 0) && (
        <CustomMarker
          icon={MapMarkerGreen}
          marker={scannerLocation}
          zIndex={2}
          info={
            <>
              <Typography
                sx={{
                  marginBottom: '6px',
                  fontSize: '12px',
                  color: '#000',
                  width: '100%',
                  textAlign: 'center',
                }}
                variant="h3"
              >
                {scannerName}
              </Typography>
              <Box
                sx={{
                  fontSize: '10px',
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                Scanner location
              </Box>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <div>
                  <Typography sx={{ fontSize: '10px' }}>
                    {`Latitude: ${scannerLocation.lat}`}
                  </Typography>
                  <Typography sx={{ fontSize: '10px' }}>
                    {`Longitude: ${scannerLocation.lng}`}
                  </Typography>
                </div>
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(`${scannerLocation.lat} ${scannerLocation.lng}`)
                    const detail = { message: t('clipboard-copied-notification.v') }
                    const event = new CustomEvent(AppEvents.HTTP_SUCCESS, { detail })
                    document.dispatchEvent(event)
                  }}
                >
                  <ContentCopyIcon className={classes.copyIcon} />
                </IconButton>
              </div>
            </>
          }
        />
      )}
      {stepLocations.map((step, index) => (
        <CustomMarker
          key={index}
          icon={MapMarker}
          marker={step}
          info={
            <>
              {step?.location_name && (
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#000' }}>
                  {step.location_name}
                </Typography>
              )}
              {step?.name && (
                <Typography sx={{ fontSize: '12px', color: '#000' }}>Step: {step.name}</Typography>
              )}
              {step.connected_account_name && (
                <>
                  <Divider sx={{ my: '8px' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {step.connected_account_img && (
                      <Avatar
                        sx={{ height: 20, width: 20 }}
                        alt={step.connected_account_name}
                        src={step.connected_account_img}
                      />
                    )}
                    <Typography sx={{ fontSize: '11px', color: '#000' }} variant="caption">
                      {step.connected_account_name}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          }
        />
      ))}
      {stepLocations.map(
        (step, index) =>
          step.radius && (
            <Circle
              key={index}
              center={step}
              options={{
                radius: step.radius,
                visible: true,
                strokeWeight: 0,
                fillOpacity: 0.3,
                clickable: false,
                draggable: false,
                editable: false,
                fillColor: theme.palette.custom.primary,
                zIndex: 1,
              }}
            />
          ),
      )}
    </GoogleMap>
  )
}
